import { FC } from 'react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import NumberFormat from 'react-number-format';

import operationTypes from 'constants/operationTypes';
import { numberFormat } from 'constants/formats';
import { useTranslate } from 'hooks/useTranslate';
import { formatNumber } from 'utils/formats';

import MandatoryField from 'modules/_shared/components/MandatoryField';
import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';
import LoadingButton from 'modules/_shared/components/LoadingButton';
import { HistoryMovementsViewProvider } from 'modules/_shared/components/HistoryMovementsView/context/HistoryMovementsViewContext';
import HistoryMovementsView from 'modules/_shared/components/HistoryMovementsView';

import { useConstitutionModal } from '../context/ConstitutionModalContext';

const ConstitutionModalMovementsView: FC = () => {
  const { t } = useTranslate();
  const animatedComponents = makeAnimated();

  const { data, actions } = useConstitutionModal();

  const validateRangeButtonDisabled =
    data.sharesToAdd <= 0 ||
    !data.selectedHoldingClass ||
    data.sharesToAdd > data.remainingShares ||
    !data.isValidateRangeEnabled;

  const handleSearchPartnerByTerm = async (searchTerm: string) => {
    const options = await actions.handleSearchPartnerByTerm(searchTerm, true);
    return options;
  };
  return (
    <>
      <label className="form-label mb-4">
        <span className="pb-2">{t('TotalNewParticipationsPen')}:</span>
        <span className="badge-primary rounded px-2 py-1 ml-2">
          {formatNumber(data.remainingShares >= 0 ? data.remainingShares : 0)}
        </span>
      </label>

      <div className="form-group">
        <label className="form-label" htmlFor="partner-select-input">
          {t('SelectPartner')} <MandatoryField />
        </label>
        <div className="form-control-wrap">
          <CustomAsyncSelect
            id="partner-select-input"
            onChange={actions.handleChangePartner}
            onFetchData={handleSearchPartnerByTerm}
            placeholder={t('SearchPartner')}
            minimumInputLength={3}
            noDataLabelKey="NoPartnersHaveBeenFound"
            loadingLabelKey="LoadingPartners"
            errorLabelKey="ErrorLoadingPartners"
            defaultOptions={data.partnerOptions}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="shares-to-add">
          {t('NºNewParticipations')} <MandatoryField />
        </label>

        <div className="input-group">
          <NumberFormat
            id="shares-to-add"
            className="form-control form-control-lg"
            value={data.sharesToAdd}
            onChange={actions.handleChangeSharesToAdd}
            thousandSeparator={numberFormat.thousandSeparator}
            decimalSeparator={numberFormat.decimalSeparator}
            allowNegative={false}
            placeholder={t('NumberOfNewParticipations')}
            decimalScale={0}
            required
          />

          <LoadingButton
            isLoading={data.isLoadingRange}
            onClick={actions.handleValidateRange}
            text={t('Validate')}
            icon="ni-check"
            className="btn btn-outline-primary btn-dim border-radius-0"
            disabled={validateRangeButtonDisabled}
          />
        </div>
      </div>

      <div className="custom-control custom-checkbox">
        <input
          type="checkbox"
          id="has-premium-checkbox"
          className="custom-control-input"
          onChange={actions.handleChangeHasPremium}
        />
        <label
          className="custom-control-label mb-2"
          htmlFor="has-premium-checkbox"
        >
          {t('PremiumAmountForParticipati')}
        </label>
      </div>

      {data.hasPremium && (
        <div className="form-group">
          <label className="form-label" htmlFor="premum">
            {t('Premium')} <MandatoryField />
          </label>

          <div className="form-control-wrap">
            <div className="form-text-hint">
              <span className="overline-title">Eur</span>
            </div>
            <NumberFormat
              id="premium"
              className="form-control form-control-lg"
              value={data.premium}
              onChange={actions.handleChangePremium}
              thousandSeparator={numberFormat.thousandSeparator}
              decimalSeparator={numberFormat.decimalSeparator}
              allowNegative={false}
              decimalScale={10}
              required
            />
          </div>
        </div>
      )}

      <div className="form-group">
        <label className="form-label" htmlFor="pay-amount">
          {t('ClassOfParticipations')} <MandatoryField />
        </label>
        <div className="form-control-wrap">
          <div className="">
            <Select
              closeMenuOnSelect
              className="react-select react-select-lg"
              defaultValue={data.defaultHoldingClassOption}
              options={data.holdingClassOptions}
              components={animatedComponents}
              onChange={actions.handleChangeHoldingClass}
            />
          </div>
        </div>
      </div>

      <div className="form-group row">
        <div className="col-6">
          <label className="form-label" htmlFor="range-from">
            {t('OfTheNumber')}
          </label>
          {data.isLoadingRange ? (
            <div className="form-control placeholder-glow h-40px d-flex justify-content-center align-items-center">
              <span className="placeholder col-4" />
            </div>
          ) : (
            <input
              id="range-from"
              type="text"
              className="form-control form-control-lg h-40px text-center"
              value={formatNumber(data.availableRange.from)}
              disabled
              readOnly
            />
          )}
        </div>

        <div className="col-6">
          <label className="form-label" htmlFor="range-to">
            {t('ToTheNumber')}
          </label>
          {data.isLoadingRange ? (
            <div className="form-control placeholder-glow h-40px d-flex justify-content-center align-items-center">
              <span className="placeholder col-4" />
            </div>
          ) : (
            <input
              id="range-to"
              type="text"
              className="form-control form-control-lg h-40px text-center"
              value={formatNumber(data.availableRange.to)}
              disabled
              readOnly
            />
          )}
        </div>
      </div>

      <div className="mt-4">
        <HistoryMovementsViewProvider
          historyMovements={data.historyMovements}
          operationType={operationTypes.CONSTITUTION}
        >
          <HistoryMovementsView />
        </HistoryMovementsViewProvider>
      </div>
    </>
  );
};

export default ConstitutionModalMovementsView;
